import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import QRCode from 'qrcode';
import { ApiError } from '../shared/api/HttpClient';
import { authProblem } from './authModel';
import type { AuthApi } from './AuthApi';
import type { AuthUser, BrowserSession, LoginEvent, TotpEnrollment } from './types';

interface AccountSecurityProps {
  api: AuthApi;
  user: AuthUser;
  onSessionEnded: (notice?: string) => void;
}

export function AccountSecurity({ api, user, onSessionEnded }: AccountSecurityProps): ReactElement {
  const [sessions, setSessions] = useState<BrowserSession[]>([]);
  const [events, setEvents] = useState<LoginEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  async function load(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const [nextSessions, nextEvents] = await Promise.all([api.sessions(), api.loginEvents()]);
      setSessions(nextSessions);
      setEvents(nextEvents);
    } catch (reason: unknown) {
      setError(authProblem(reason));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [api]);

  async function beginEnrollment(): Promise<void> {
    setPending('enroll');
    setError(null);
    try {
      const result = await api.beginTotpEnrollment();
      setEnrollment(result);
      setQrCode(await QRCode.toDataURL(result.provisioningUri, { width: 220, margin: 1, color: { dark: '#0b0d0fff', light: '#ffffffff' } }));
    } catch (reason: unknown) {
      setError(authProblem(reason));
    } finally {
      setPending(null);
    }
  }

  async function confirmEnrollment(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setPending('confirm');
    setError(null);
    try {
      const result = await api.confirmTotpEnrollment(confirmationCode.trim());
      setRecoveryCodes(result.recoveryCodes);
      setEnrollment(null);
      setQrCode(null);
      setConfirmationCode('');
    } catch (reason: unknown) {
      setError(authProblem(reason));
    } finally {
      setPending(null);
    }
  }

  async function disableTotp(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setPending('disable');
    setError(null);
    try {
      await api.disableTotp(disablePassword, disableCode.trim());
      onSessionEnded('Двофакторний захист вимкнено. Увійдіть знову.');
    } catch (reason: unknown) {
      setError(authProblem(reason));
      setPending(null);
    }
  }

  async function changePassword(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError('Новий пароль і підтвердження не збігаються.');
      return;
    }
    setPending('password');
    try {
      await api.changePassword(currentPassword, newPassword);
      onSessionEnded('Пароль змінено, усі попередні сесії відкликано. Увійдіть новим паролем.');
    } catch (reason: unknown) {
      setError(reason instanceof ApiError && reason.problem.code === 'invalid_credentials' ? 'Поточний пароль неправильний.' : authProblem(reason));
      setPending(null);
    }
  }

  async function revoke(session: BrowserSession): Promise<void> {
    setPending(session.id);
    setError(null);
    try {
      await api.revokeSession(session.id);
      if (session.current) {
        onSessionEnded('Поточну сесію відкликано.');
        return;
      }
      setSessions((items) => items.filter((item) => item.id !== session.id));
      setNotice('Вибрану сесію відкликано.');
    } catch (reason: unknown) {
      setError(authProblem(reason));
    } finally {
      setPending(null);
    }
  }

  function copyRecoveryCodes(): void {
    if (recoveryCodes === null) return;
    void navigator.clipboard.writeText(recoveryCodes.join('\n')).then(() => setNotice('Резервні коди скопійовано.')).catch(() => setError('Не вдалося скопіювати. Виділіть і збережіть коди вручну.'));
  }

  if (recoveryCodes !== null) return <main className="security-page recovery-page">
    <section className="recovery-sheet" aria-labelledby="recovery-title">
      <p className="eyebrow">ОДНОРАЗОВИЙ ПОКАЗ</p>
      <h1 id="recovery-title">Збережіть резервні коди</h1>
      <p>Кожен код працює лише один раз. Зберігайте їх окремо від телефону — після закриття цієї сторінки платформа більше їх не покаже.</p>
      <ol>{recoveryCodes.map((code) => <li key={code}><code>{code}</code></li>)}</ol>
      {notice !== null && <div className="security-notice" role="status">{notice}</div>}
      <div className="recovery-actions"><button className="button secondary" type="button" onClick={copyRecoveryCodes}>Скопіювати коди</button><button className="button primary" type="button" onClick={() => onSessionEnded('2FA увімкнено. Увійдіть з кодом автентифікатора.')}>Я зберіг коди — увійти знову</button></div>
    </section>
  </main>;

  return <main className="security-page">
    <header className="security-heading">
      <div><p className="eyebrow">ОБЛІКОВИЙ ЗАПИС</p><h1>Безпека й активні входи</h1><p>{user.displayName} · {user.email}</p></div>
      <a href="/admin">← Повернутися до ефіру</a>
    </header>

    {error !== null && <div className="security-error" role="alert"><strong>Дію не виконано</strong><span>{error}</span><button type="button" onClick={() => setError(null)}>Закрити</button></div>}
    {notice !== null && <div className="security-notice" role="status">{notice}</div>}

    <div className="security-layout">
      <section className="security-section" aria-labelledby="two-factor-title">
        <header><div><p className="eyebrow">ДРУГИЙ ФАКТОР</p><h2 id="two-factor-title">Застосунок-автентифікатор</h2></div><span className={user.totpEnabled ? 'security-state enabled' : 'security-state'}><i />{user.totpEnabled ? 'УВІМКНЕНО' : 'ВИМКНЕНО'}</span></header>
        {!user.totpEnabled && enrollment === null && <div className="security-copy"><p>Додайте другий фактор перед запуском платформи в роботу. Після налаштування всі наявні сесії буде завершено.</p><button className="button primary" type="button" disabled={pending !== null} onClick={() => void beginEnrollment()}>Налаштувати 2FA</button></div>}
        {!user.totpEnabled && enrollment !== null && <form className="enrollment-form" onSubmit={(event) => void confirmEnrollment(event)}>
          <div className="enrollment-qr">{qrCode === null ? <span>Створюю QR…</span> : <img src={qrCode} alt="QR-код для застосунку-автентифікатора" />}</div>
          <div><p><strong>1.</strong> Відскануйте QR-код у вашому застосунку.</p><p><strong>2.</strong> Якщо сканування недоступне, введіть ключ:</p><code className="enrollment-secret">{enrollment.secret}</code><label htmlFor="enrollment-code">3. Код із застосунку</label><input id="enrollment-code" className="totp-input" inputMode="numeric" autoComplete="one-time-code" required value={confirmationCode} onChange={(event) => setConfirmationCode(event.target.value)} placeholder="000 000" /><div className="enrollment-actions"><button className="button secondary" type="button" onClick={() => { setEnrollment(null); setQrCode(null); }}>Скасувати</button><button className="button primary" type="submit" disabled={pending !== null}>{pending === 'confirm' ? 'ПЕРЕВІРЯЮ…' : 'УВІМКНУТИ 2FA'}</button></div></div>
        </form>}
        {user.totpEnabled && <form className="disable-form" onSubmit={(event) => void disableTotp(event)}><p>Вимкнення потребує пароля та поточного TOTP або резервного коду. Усі сесії буде завершено.</p><div><label htmlFor="disable-password">Пароль<input id="disable-password" type="password" autoComplete="current-password" required value={disablePassword} onChange={(event) => setDisablePassword(event.target.value)} /></label><label htmlFor="disable-code">Код<input id="disable-code" type="text" autoComplete="one-time-code" required value={disableCode} onChange={(event) => setDisableCode(event.target.value)} /></label></div><button className="danger-button" type="submit" disabled={pending !== null}>{pending === 'disable' ? 'ПЕРЕВІРЯЮ…' : 'ВИМКНУТИ 2FA'}</button></form>}
      </section>

      <section className="security-section" aria-labelledby="sessions-title">
        <header><div><p className="eyebrow">ДОСТУП</p><h2 id="sessions-title">Активні сесії</h2></div><button className="security-refresh" type="button" onClick={() => void load()} disabled={loading}>Оновити</button></header>
        {loading ? <div className="security-loading"><i /> Завантажую сесії…</div> : <div className="session-list">
          {sessions.map((session) => <article key={session.id}><div className="session-icon">{session.current ? '●' : '○'}</div><div><strong>{session.current ? 'Цей браузер' : 'Інший вхід'}</strong><span>Активність: {formatDate(session.lastSeenAt)}</span><small>Завершення: {formatDate(session.expiresAt)}</small></div><button type="button" disabled={pending !== null} onClick={() => void revoke(session)}>{pending === session.id ? 'Відкликаю…' : 'Відкликати'}</button></article>)}
          {sessions.length === 0 && <p className="security-empty">Активних сесій не знайдено.</p>}
        </div>}
      </section>

      <section className="security-section" aria-labelledby="password-title">
        <header><div><p className="eyebrow">ПАРОЛЬ</p><h2 id="password-title">Змінити пароль</h2></div><span>МІНІМУМ 14 СИМВОЛІВ</span></header>
        <form className="password-change-form" onSubmit={(event) => void changePassword(event)}>
          <p>Після зміни пароля платформа завершить усі активні входи, включно з цим браузером.</p>
          <label htmlFor="current-password">Поточний пароль<input id="current-password" type="password" autoComplete="current-password" required value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} /></label>
          <label htmlFor="new-password">Новий пароль<input id="new-password" type="password" autoComplete="new-password" minLength={14} required value={newPassword} onChange={(event) => setNewPassword(event.target.value)} /></label>
          <label htmlFor="confirm-password">Повторіть новий пароль<input id="confirm-password" type="password" autoComplete="new-password" minLength={14} required value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} /></label>
          <button className="button primary" type="submit" disabled={pending !== null}>{pending === 'password' ? 'ЗМІНЮЮ…' : 'ЗМІНИТИ ПАРОЛЬ'}</button>
        </form>
      </section>

      <section className="security-section login-history" aria-labelledby="history-title">
        <header><div><p className="eyebrow">ЖУРНАЛ</p><h2 id="history-title">Останні спроби входу</h2></div><span>20 останніх</span></header>
        {loading ? <div className="security-loading"><i /> Завантажую журнал…</div> : <div className="history-list">
          {events.map((event) => <article key={event.id}><i className={`history-dot outcome-${event.outcome.toLowerCase()}`} /><strong>{outcomeLabel(event.outcome)}</strong><span>{reasonLabel(event.reasonCode)}</span><time>{formatDate(event.createdAt)}</time></article>)}
          {events.length === 0 && <p className="security-empty">Записів ще немає.</p>}
        </div>}
      </section>
    </div>
  </main>;
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('uk-UA', { dateStyle: 'short', timeStyle: 'medium' }).format(new Date(value));
}

function outcomeLabel(outcome: LoginEvent['outcome']): string {
  if (outcome === 'SUCCESS') return 'Вхід виконано';
  if (outcome === 'CHALLENGE') return 'Запитано другий фактор';
  return 'Вхід відхилено';
}

function reasonLabel(reason: string | null): string {
  if (reason === 'invalid_credentials') return 'Неправильні облікові дані';
  if (reason === 'invalid_second_factor') return 'Неправильний другий фактор';
  if (reason === 'totp_required') return 'Очікування TOTP або резервного коду';
  return reason === null ? 'Перевірку завершено' : reason;
}
